import { Draft } from 'immer';
import { Area, Sheet } from '../../types';
import {
  ValidationAddressMap,
  ValidationMatrix,
  ValidationRule,
  ValidationRules,
} from '../../types/sheet/dataValidation';
import { SheetManagementSet } from '../../types/sheet/draft';
import {
  RemoveCellDataValidation,
  ApplyCellDataValidation,
} from '../../types/commands/sheetCommands/sheetCommands';
import {
  addAreasToExistingAreas,
  combineConsecutiveAreas,
  convertArrayAreaAddressToArea,
  createArrayAreaAddressFromArea,
  getIntersectedArea,
  mergeAreas,
  reduceAreaByIntersection,
} from '../area/area';
import {
  addAddressToRangeMatrix,
  removeAddressFromRangeMatrix,
} from '../address/address';
import _ from 'lodash';
import { convertAreaToAddressString } from '../address/singleAddress';
import {
  applyDataValidationCommand,
  removeDataValidationCommand,
} from '../commands/generate/generateSheetCommands';

export async function applyDataValidation(
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: ApplyCellDataValidation
) {
  const { sheetId, rules } = command;

  console.log(
    'newRules',
    rules.map((rule) => {
      const areas = rule.areas.map((area) =>
        convertAreaToAddressString(area, 10000, 10000)
      );
      return {
        ...rule,
        areas,
      };
    })
  );

  const { dataValidationMatrix, dataValidationsMap, dataValidationRules } =
    sheet;

  const oldRules: ValidationRule[] = [];
  const rulesToRemove: string[] = [];

  for (const rule of rules) {
    await startUpdateDataValidation(
      dataValidationMatrix,
      dataValidationsMap,
      dataValidationRules,
      rule,
      oldRules,
      rulesToRemove
    );
  }

  console.log(
    'oldRules',
    oldRules.map((rule) => {
      const areas = rule.areas.map((area) =>
        convertAreaToAddressString(area, 10000, 10000)
      );
      return {
        ...rule,
        areas,
      };
    })
  );

  console.log('rulesToRemove', rulesToRemove);

  applyDataValidationCommand(set, sheetId, command, oldRules, rulesToRemove);
}

export async function removeDataValidation(
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: RemoveCellDataValidation
) {
  const { sheetId, ruleIds } = command;

  console.log('Rules To Remove', ruleIds);

  const { dataValidationMatrix, dataValidationsMap, dataValidationRules } =
    sheet;

  const oldRules: ValidationRule[] = [];

  for (const ruleId of ruleIds) {
    const rule = dataValidationRules[ruleId];
    if (rule) {
      oldRules.push(_.cloneDeep(rule));

      const areas = rule.areas;

      for (const area of areas) {
        removeIntersectedRules(
          dataValidationMatrix,
          dataValidationsMap,
          dataValidationRules,
          area,
          oldRules
        );
      }
    }
  }

  console.log(
    'oldRules',
    oldRules.map((rule) => {
      const areas = rule.areas.map((area) =>
        convertAreaToAddressString(area, 10000, 10000)
      );
      return {
        ...rule,
        areas,
      };
    })
  );

  removeDataValidationCommand(set, sheetId, command, oldRules);
}

// #region helpers
// Scenario #1 Updating an Existing Rule
// Scenario #2 Adding a new rule | Rule Schema Matches an already existing rule => Insert New Areas
// Scenario #3 New Rule Scehma does not match any existed rules => Save it as new
async function startUpdateDataValidation(
  matrix: ValidationMatrix,
  map: ValidationAddressMap,
  rules: ValidationRules,
  newRule: ValidationRule,
  oldRules: ValidationRule[],
  rulesToRemove: string[]
) {
  let { id: newRuleId, areas: newRuleAreas } = newRule;
  const existingRule = rules[newRuleId];
  let areasToRemove = newRuleAreas;
  if (existingRule) {
    areasToRemove = combineConsecutiveAreas([
      ...existingRule.areas,
      ...newRuleAreas,
    ]);
    // console.log('areas to remove', areasToRemove);
    // console.log("existingRule", existingRule)
  }
  // let areasToRemove =  existingRule?.areas ?? newRuleAreas;

  //   Remove Intersected Areas
  for (const area of areasToRemove) {
    removeIntersectedRules(matrix, map, rules, area, oldRules);
  }

  let ruleId = '';
  if (existingRule) {
    const existingRuleId = existingRule.id;

    const isSame = _.isEqual(
      { ...existingRule, areas: [] },
      { ...newRule, areas: [] }
    );

    // If existing rule is not same as the old one(id may be the same) then remove rule
    saveDataValidationInHistory(rules, existingRuleId, oldRules);
    if (!isSame) {
      delete rules[existingRuleId];
    } else {
      // Else Save Rule with old areas in history
      existingRule.areas = newRuleAreas;

      // In case if rules gets deleted by removeIntersectedRules then re-save it (if there are no addresses in the rule the rule gets deleted)
      if (!rules[existingRuleId]) {
        rules[existingRuleId] = existingRule;
      }

      ruleId = existingRuleId;
    }
  }

  const matchingRule = getMatchingRule(rules, newRule);
  // If matching rule and existing rules are not same
  if (matchingRule && ruleId !== matchingRule.id) {
    ruleId = matchingRule.id;

    // Save Rule with old areas in history
    saveDataValidationInHistory(rules, ruleId, oldRules);

    //merge new areas
    const { mergedAreas, uniqueAreas } = addAreasToExistingAreas(
      matchingRule.areas,
      newRuleAreas
    );
    matchingRule.areas = mergedAreas;
    newRuleAreas = uniqueAreas;
  }

  if (!ruleId) {
    // Save Rule
    rules[newRuleId] = { ...newRule };
    ruleId = newRuleId;
    rulesToRemove.push(ruleId);
  }

  for (const area of newRuleAreas) {
    saveNonIntersectedValidation(matrix, map, area, ruleId);
  }
}

function removeIntersectedRules(
  matrix: ValidationMatrix,
  map: ValidationAddressMap,
  rules: ValidationRules,
  newArea: Area,
  oldRules: ValidationRule[]
) {
  const existingValidations = Object.keys(map);
  for (const existingAddress of existingValidations) {
    const existingRuleId = map[existingAddress];
    const existingArea = convertArrayAreaAddressToArea(existingAddress);
    if (!existingArea) continue;

    const intersection = getIntersectedArea(newArea, existingArea);
    if (intersection) {
      // Remove from current matrix
      removeAddressFromRangeMatrix(matrix, existingArea, existingAddress);
      reduceAreaByIntersection(existingArea, intersection).forEach((area) =>
        saveNonIntersectedValidation(matrix, map, area, existingRuleId)
      );
      saveDataValidationInHistory(rules, existingRuleId, oldRules);
      removeIntersectionFromValidationRule(rules, existingRuleId, intersection);

      delete map[existingAddress];
    }
  }
}

function saveNonIntersectedValidation(
  matrix: ValidationMatrix,
  map: ValidationAddressMap,
  area: Area,
  ruleId: string
) {
  const arrayAreaAddress = createArrayAreaAddressFromArea(area);
  addAddressToRangeMatrix(matrix, area, arrayAreaAddress);
  map[arrayAreaAddress] = ruleId;
}

function saveDataValidationInHistory(
  rules: ValidationRules,
  id: string,
  oldRules: ValidationRule[]
) {
  const isExist = oldRules.findIndex((rule) => rule.id === id);
  if (isExist === -1) {
    const rule = rules[id];
    if (rule) oldRules.push(_.cloneDeep(rule));
  }
}

function removeIntersectionFromValidationRule(
  rules: ValidationRules,
  ruleId: string,
  intersection: Area
) {
  const rule = rules[ruleId];
  if (rule) {
    const areas = rule.areas;
    const newAreas: Area[] = [];
    for (const area of areas) {
      reduceAreaByIntersection(area, intersection).forEach((newArea) =>
        newAreas.push(newArea)
      );
    }
    if (newAreas.length) {
      rule.areas = newAreas;
    } else {
      delete rules[ruleId];
    }
  }
}

function getMatchingRule(rules: ValidationRules, newRule: ValidationRule) {
  const existingRules = Object.values(rules);
  const newRuleWithoutId = { ...newRule, id: '', areas: [] };

  return existingRules.find((rule) => {
    const ruleWithoutId = { ...rule, id: '', areas: [] };

    return _.isEqual(ruleWithoutId, newRuleWithoutId);
  });
}
// #endregion
